import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { API, urlBase } from "../APICalls";

import lebenRaffleIcon from "../assets/logo.png";
import SmallBox from "../components/SmallBox";
import NumberSelectionSimulator from "../components/NumberSelectionSimulator";
import LinkBox from "../components/LinkBox";
import ActionBox from "../components/ActionBox";
import FileUploader from "../components/FileUploader";

export default function Raffle() {
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  document.getElementsByTagName("body")[0].classList.remove("login-page");
  document.getElementsByTagName("body")[0].classList.add("sidebar-mini");

  const [raffle, setRaffle] = useState(null);

  const [processingRaffle, setProcessingRaffle] = useState(false);

  const [downloadingOrigin, setDownloadingOrigin] = useState(false);
  const [downloadingWinners, setDownloadingWinners] = useState(false);
  const [downloadingBench, setDownloadingBench] = useState(false);

  const [raffleForm, setRaffleForm] = useState(null);
  const [raffleFormToken, setRaffleFormToken] = useState(null);
  const [editingRaffleForm, setEditingRaffleForm] = useState(false);
  const [raffleFormTitle, setRaffleFormTitle] = useState("");
  const [raffleFormDescription, setRaffleFormDescription] = useState("");
  const [raffleFormImageFile, setRaffleFormImageFile] = useState(null);
  const [raffleFormExpiresIn, setRaffleFormExpiresIn] = useState(0);
  const [raffleFormAttachmentLabel, setRaffleFormAttachmentLabel] =
    useState("");
  const [raffleFormRequiresAttachment, setRaffleFormRequiresAttachment] =
    useState(false);

  const [raffleFormAdditionalField1, setRaffleFormAdditionalField1] =
    useState("");
  const [raffleFormAdditionalField2, setRaffleFormAdditionalField2] =
    useState("");
  const [raffleFormAdditionalField3, setRaffleFormAdditionalField3] =
    useState("");
  const [
    raffleFormAdditionalField1Values,
    setRaffleFormAdditionalField1Values,
  ] = useState("");
  const [
    raffleFormAdditionalField2Values,
    setRaffleFormAdditionalField2Values,
  ] = useState("");
  const [
    raffleFormAdditionalField3Values,
    setRaffleFormAdditionalField3Values,
  ] = useState("");

  const [downloadingRegistries, setDownloadingRegistries] = useState(false);

  const [raffleFormRegistries, setRaffleFormRegistries] = useState([]);

  var { raffleId } = useParams();

  useEffect(() => {
    API.GetRaffle(raffleId)
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setRaffle(data);
          });
        } else {
          alert("Token is not valid");
        }
      })
      .catch(() => {
        alert("Cannot connect to LebenRaffle API");
      });
  }, []);

  useEffect(() => {
    API.GetForm(raffleId)
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            if (data && data.form) {
              setRaffleFormToken(data.token);
              setRaffleForm(data.form);
              setRaffleFormTitle(data.form.Title);
              setRaffleFormDescription(data.form.Description);
              setRaffleFormImageFile(data.form.ImageFile);
              setRaffleFormExpiresIn(data.form.ExpiresIn);
              setRaffleFormRequiresAttachment(data.form.RequiresAttachment);
              setRaffleFormAttachmentLabel(data.form.AttachmentLabel);
              setRaffleFormAdditionalField1(data.form.AdditionalField1);
              setRaffleFormAdditionalField2(data.form.AdditionalField2);
              setRaffleFormAdditionalField3(data.form.AdditionalField3);
              setRaffleFormAdditionalField1Values(
                data.form.AdditionalField1Values
              );
              setRaffleFormAdditionalField2Values(
                data.form.AdditionalField2Values
              );
              setRaffleFormAdditionalField3Values(
                data.form.AdditionalField3Values
              );
              API.GetFormRegistries(data.form.Id).then((response) => {
                if (response.ok) {
                  response.json().then((data) => {
                    setRaffleFormRegistries(data);
                  });
                }
              });
            }
          });
        }
      })
      .catch(() => {
        alert("Cannot connect to LebenRaffle API");
      });
  }, []);

  const processRaffle = () => {
    API.ProcessRaffle(raffleId)
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setProcessingRaffle(true);
            setRaffle(data);
            sleep(3000).then(() => {
              setProcessingRaffle(false);
            });
          });
        } else {
          alert("Token is not valid");
        }
      })
      .catch(() => {
        alert("Cannot connect to LebenRaffle API");
      });
  };

  const resetRaffle = () => {
    API.ResetRaffle(raffleId)
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setRaffle(data);
          });
        } else {
          alert("Token is not valid");
        }
      })
      .catch(() => {
        alert("Cannot connect to LebenRaffle API");
      });
  };

  const downloadBlob = (blob, filename) => {
    var url = window.URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();
    a.remove(); //afterwards we remove the element again
  };

  const downloadOriginDatabase = () => {
    if (raffle != null && raffle.FileId != null) {
      setDownloadingOrigin(true);
      var filename = "download";
      API.GetRaffleResultFile(raffle.Id, "all")
        .then((response) => {
          filename = response.headers
            .get("Content-Disposition")
            .replace("attachment; filename=", "");
          return response.blob();
        })
        .then((blob) => {
          downloadBlob(blob, filename);
        })
        .finally(() => {
          setDownloadingOrigin(false);
        });
    }
  };

  const downloadWinnersDatabase = () => {
    if (raffle != null && raffle.FileId != null) {
      setDownloadingWinners(true);
      var filename = "download";
      API.GetRaffleResultFile(raffle.Id, "winners")
        .then((response) => {
          filename = response.headers
            .get("Content-Disposition")
            .replace("attachment; filename=", "");
          return response.blob();
        })
        .then((blob) => {
          downloadBlob(blob, filename);
        })
        .finally(() => {
          setDownloadingWinners(false);
        });
    }
  };

  const downloadBenchDatabase = () => {
    if (raffle != null && raffle.FileId != null) {
      setDownloadingBench(true);
      var filename = "download";
      API.GetRaffleResultFile(raffle.Id, "bench")
        .then((response) => {
          filename = response.headers
            .get("Content-Disposition")
            .replace("attachment; filename=", "");
          return response.blob();
        })
        .then((blob) => {
          downloadBlob(blob, filename);
        })
        .finally(() => {
          setDownloadingBench(false);
        });
    }
  };

  const saveRaffleForm = () => {
    if (raffleFormTitle === "" || raffleFormDescription === "") {
      alert("Es obligatorio completar título y descripción");
      return;
    }
    if (raffleFormRequiresAttachment && raffleFormAttachmentLabel === "") {
      alert(
        "Si se requiere un archivo adjunto, es obligatorio completar la etiqueta del mismo"
      );
      return;
    }
    if (raffleForm && raffleForm.Id) {
      // edit
      API.EditForm(
        raffleForm.Id,
        raffleFormTitle,
        raffleFormDescription,
        raffleFormImageFile,
        raffleFormExpiresIn,
        raffleFormRequiresAttachment,
        raffleFormRequiresAttachment ? raffleFormAttachmentLabel : "",
        raffleFormAdditionalField1,
        raffleFormAdditionalField2,
        raffleFormAdditionalField3,
        raffleFormAdditionalField1Values,
        raffleFormAdditionalField2Values,
        raffleFormAdditionalField3Values
      ).then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setRaffleForm(data.form);
            setRaffleFormToken(data.token);
            setEditingRaffleForm(false);
          });
        }
      });
    } else {
      API.CreateForm(
        raffleId,
        raffleFormTitle,
        raffleFormDescription,
        raffleFormImageFile,
        raffleFormExpiresIn,
        raffleFormRequiresAttachment,
        raffleFormRequiresAttachment ? raffleFormAttachmentLabel : "",
        raffleFormAdditionalField1,
        raffleFormAdditionalField2,
        raffleFormAdditionalField3,
        raffleFormAdditionalField1Values,
        raffleFormAdditionalField2Values,
        raffleFormAdditionalField3Values
      ).then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setRaffleForm(data.form);
            setRaffleFormToken(data.token);
            setEditingRaffleForm(false);
          });
        }
      });
    }
  };

  const imageUploaded = (file) => {
    setRaffleFormImageFile(file.Id);
  };

  const [copyLinkButtonText, setCopyLinkButtonText] = useState("Copiar enlace");
  const onCopyLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/form/${raffleFormToken}`
    );

    // show text for 3 seconds
    setCopyLinkButtonText("¡Enlace copiado!");
    setTimeout(() => {
      setCopyLinkButtonText("Copiar enlace");
    }, 1500);
  };

  const onOpenLink = () => {
    window.open(`${window.location.origin}/form/${raffleFormToken}`, "_blank");
  };

  const downloadFormRegistries = () => {
    if (raffle != null && raffleForm != null) {
      setDownloadingRegistries(true);
      var filename = "download";
      API.GetFormRegistriesFile(raffleForm.Id)
        .then((response) => {
          filename = response.headers
            .get("Content-Disposition")
            .replace("attachment; filename=", "");
          return response.blob();
        })
        .then((blob) => {
          downloadBlob(blob, filename);
        })
        .finally(() => {
          setDownloadingRegistries(false);
        });
    }
  };

  if (raffle == null) {
    return (
      <div className="preloader flex-column justify-content-center align-items-center">
        <img
          className="animation__shake"
          src={lebenRaffleIcon}
          alt="LebenRaffle Logo"
          height="60"
          width="60"
        />
      </div>
    );
  } else {
    return (
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Sorteo: {raffle.Name}</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/raffles">Todos los sorteos</Link>
                  </li>
                  <li className="breadcrumb-item active">Sorteo</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-6">
                <div className="card card-primary card-outline">
                  <div className="card-header">
                    <h5 className="m-0">Detalles</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-lg-4 col-6">
                        <SmallBox
                          text="# Participantes"
                          value={raffle.ParticipantNumber}
                          bgColorClass="bg-info"
                          icon="fas fa-users"
                        />
                      </div>
                      <div className="col-lg-4 col-6">
                        <SmallBox
                          text="# Ganadores"
                          value={
                            raffle.WinnerNumber + " + " + raffle.BenchNumber
                          }
                          bgColorClass="bg-success"
                          icon="fas fa-award"
                        />
                      </div>
                      <div className="col-lg-4 col-6">
                        <SmallBox
                          text="Fecha de registro"
                          value={moment(raffle.CreationDatetime).format(
                            "DD/MM/YYYY"
                          )}
                          bgColorClass="bg-warning"
                          icon="far fa-calendar-alt"
                        />
                      </div>
                      {raffle.FileId == null ? (
                        <></>
                      ) : (
                        <div className="col-lg-12 col-12">
                          {downloadingOrigin ? (
                            <SmallBox
                              text="Descargando fichero..."
                              value="Participantes"
                              bgColorClass="bg-teal"
                              icon="fas fa-file-csv"
                            />
                          ) : (
                            <ActionBox
                              text="Base de datos en CSV"
                              value="Participantes"
                              bgColorClass="bg-teal"
                              icon="fas fa-file-csv"
                              action={downloadOriginDatabase}
                              actionText="Descargar"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card card-success card-outline">
                  <div className="card-header">
                    <h5 className="m-0">Resultado</h5>
                  </div>
                  <div className="card-body">
                    {raffle.RaffleDatetime == null ? (
                      <>
                        <p className="lead">
                          Todavía no se ha realizado el sorteo
                        </p>
                        <hr />
                        <a
                          href="#"
                          onClick={processRaffle}
                          className="btn btn-primary"
                        >
                          Iniciar Sorteo
                        </a>
                      </>
                    ) : (
                      <>
                        {processingRaffle ? (
                          <NumberSelectionSimulator
                            numbers={raffle.WinnerNumber + raffle.BenchNumber}
                          />
                        ) : raffle.FileId == null ? (
                          <>
                            <h3 className="text-success">
                              Ganadores:{" "}
                              {raffle.RaffleResult.split(", ")
                                .slice(0, raffle.WinnerNumber)
                                .join(", ")}
                            </h3>
                            <h3 className="text-muted">
                              Suplentes:{" "}
                              {raffle.RaffleResult.split(", ")
                                .slice(raffle.WinnerNumber)
                                .join(", ")}
                            </h3>
                          </>
                        ) : (
                          <div className="row">
                            <div className="col-lg-6 col-6">
                              {downloadingWinners ? (
                                <SmallBox
                                  text="Descargando fichero..."
                                  value="Ganadores"
                                  bgColorClass="bg-success"
                                  icon="fas fa-file-csv"
                                />
                              ) : (
                                <ActionBox
                                  text="Base de datos en CSV"
                                  value="Ganadores"
                                  bgColorClass="bg-success"
                                  icon="fas fa-file-csv"
                                  action={downloadWinnersDatabase}
                                  actionText="Descargar"
                                />
                              )}
                            </div>
                            <div className="col-lg-6 col-6">
                              {downloadingBench ? (
                                <SmallBox
                                  text="Descargando fichero..."
                                  value="Suplentes"
                                  bgColorClass="bg-secondary"
                                  icon="fas fa-file-csv"
                                />
                              ) : (
                                <ActionBox
                                  text="Base de datos en CSV"
                                  value="Suplentes"
                                  bgColorClass="bg-secondary"
                                  icon="fas fa-file-csv"
                                  action={downloadBenchDatabase}
                                  actionText="Descargar"
                                />
                              )}
                            </div>
                          </div>
                        )}
                        <hr />
                        <p className="lead">
                          {moment(raffle.CreationDatetime).format(
                            "[Sorteo realizado el ] DD/MM/YYYY [ a las ] HH:mm"
                          )}
                        </p>
                        <hr />
                        {processingRaffle ? (
                          <a href="#" className="btn disabled btn-primary">
                            Procesando Sorteo...
                          </a>
                        ) : (
                          <></>
                          // <a href="#" onClick={resetRaffle} className="btn btn-danger">Reiniciar Sorteo</a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                <div className="card card-info card-outline">
                  <div className="card-header">
                    <h5 className="m-0">Formulario de confirmación</h5>
                  </div>
                  {!raffleForm && !editingRaffleForm && (
                    <div className="card-body">
                      <p className="lead">
                        No se ha creado un formulario de confirmación para este
                        sorteo. Puedes configurar uno a continuación.
                      </p>
                      <button
                        type="button"
                        className="btn btn-block btn-outline-danger btn-lg"
                        onClick={() => setEditingRaffleForm(true)}
                      >
                        Configurar Formulario
                      </button>
                    </div>
                  )}
                  {raffleForm && !editingRaffleForm && (
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-6">
                          <h3>{raffleForm.Title}</h3>
                          <strong>
                            {raffleForm.ExpiresIn === 0
                              ? "El formulario no caduca"
                              : `Caduca después de ${raffleForm.ExpiresIn} días`}
                          </strong>
                          {raffleFormImageFile && (
                            <img
                              src={
                                urlBase +
                                "/api/PublicFile/" +
                                raffleFormImageFile
                              }
                              className="img-fluid"
                            />
                          )}
                          <p
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                raffleForm.Description
                              ),
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-block btn-outline-danger btn-lg"
                            onClick={() => setEditingRaffleForm(true)}
                          >
                            Editar Formulario
                          </button>
                        </div>
                        <div className="col-lg-6">
                          {raffleFormToken && (
                            <>
                              <div class="alert alert-info">
                                <h5>
                                  <i class="icon fas fa-info"></i> Enlace
                                </h5>
                                <p>
                                  Para compartir el formulario con los
                                  participantes, copia el siguiente enlace.
                                  Recuerda que este enlace es válido durante{" "}
                                  {raffleForm.ExpiresIn === 0
                                    ? "(no caduca)"
                                    : `${raffleForm.ExpiresIn} días`}{" "}
                                  desde hoy.
                                </p>
                                <strong>
                                  {window.location.origin}/form/
                                  {raffleFormToken}
                                </strong>
                                <div className="row mt-4">
                                  <div className="col-lg-6">
                                    <button
                                      type="button"
                                      className="btn btn-block btn-secondary"
                                      onClick={onCopyLink}
                                    >
                                      <i className="fas fa-copy mr-4"></i>
                                      {copyLinkButtonText}
                                    </button>
                                  </div>
                                  <div className="col-lg-6">
                                    <button
                                      type="button"
                                      className="btn btn-block btn-secondary"
                                      onClick={onOpenLink}
                                    >
                                      <i className="fas fa-external-link-alt mr-4"></i>
                                      Abrir en ventana nueva
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-block btn-outline-info"
                                onClick={downloadFormRegistries}
                              >
                                <i className="fa fa-download mr-4"></i>
                                Desgargar registros de confirmación
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {raffleFormRegistries.length > 0 && (
                        <div className="row mt-4">
                          <h3>Registros de aceptación del premio</h3>
                          <div className="col-lg-12">
                            <table
                              id="registriesTable"
                              className="table table-bordered"
                            >
                              <thead>
                                <tr>
                                  <th>Nombre</th>
                                  <th>Apellidos</th>
                                  <th>Email</th>
                                  <th>Teléfono</th>
                                  <th>DNI</th>
                                  <th>Fecha Registro</th>
                                  <th>Archivo adjunto</th>
                                </tr>
                              </thead>
                              <tbody>
                                {raffleFormRegistries.map((item, i) => {
                                  let objectUrl = null;
                                  if (
                                    raffleForm.RequiresAttachment &&
                                    item.AttachedFile
                                  ) {
                                    const blob = new Blob(
                                      [b64ToBlob(item.AttachedFile)],
                                      {
                                        type: "application/octet-stream",
                                      }
                                    );
                                    objectUrl = URL.createObjectURL(blob);
                                  }
                                  return (
                                    <tr key={i}>
                                      <td>{item.Name}</td>
                                      <td>{item.Surname}</td>
                                      <td>{item.Email}</td>
                                      <td>{item.Phone}</td>
                                      <td>{item.NationalId}</td>
                                      <td>
                                        {moment(item.RegisteredAt).format(
                                          "DD/MM/YYYY HH:mm"
                                        )}
                                      </td>
                                      <td>
                                        {objectUrl && (
                                          <a
                                            href={objectUrl}
                                            download={
                                              item.AttachedFileName || "archivo"
                                            }
                                            className="btn btn-primary"
                                          >
                                            Ver
                                          </a>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {editingRaffleForm && (
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Título</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Nombre del sorteo"
                              value={raffleFormTitle}
                              onChange={(e) =>
                                setRaffleFormTitle(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="col-sm-12">
                          <div className="form-group">
                            <label>Días activo (0 no caduca) </label>
                            <input
                              type="number"
                              min={0}
                              className="form-control"
                              placeholder="0"
                              value={raffleFormExpiresIn}
                              onChange={(e) =>
                                setRaffleFormExpiresIn(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="col-sm-12">
                          <div className="form-group">
                            <label>Imagen</label>
                            {raffleFormImageFile && (
                              <img
                                src={
                                  urlBase +
                                  "/api/PublicFile/" +
                                  raffleFormImageFile
                                }
                                className="img-fluid"
                              />
                            )}
                            <FileUploader onFileUploadDone={imageUploaded} />
                          </div>
                        </div>
                        <div className="col-sm-12">
                          <div className="form-group">
                            <label>Descripción</label>
                            <ReactQuill
                              theme="snow"
                              placeholder={
                                "Escribe una descripción para mostrar en el formulario..."
                              }
                              value={raffleFormDescription}
                              onChange={setRaffleFormDescription}
                            />
                          </div>
                        </div>
                        <div className="col-sm-12">
                          <label>Requerir archivo adjunto</label>
                          <div className="form-group">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={raffleFormRequiresAttachment}
                                onChange={(e) =>
                                  setRaffleFormRequiresAttachment(
                                    e.target.checked
                                  )
                                }
                              />
                              <label className="form-check-label">
                                El formulario requiere que el participante
                                adjunte un archivo
                              </label>
                            </div>
                          </div>
                        </div>
                        {raffleFormRequiresAttachment && (
                          <div className="col-sm-12">
                            <div className="form-group">
                              <label>Etiqueta para archivo adjunto</label>
                              <textarea
                                className="form-control"
                                placeholder={
                                  "Escribe un texto explicando qué archivo debe subir el participante y con qué objetivo..."
                                }
                                rows={5}
                                value={raffleFormAttachmentLabel}
                                onChange={(e) =>
                                  setRaffleFormAttachmentLabel(e.target.value)
                                }
                              />
                            </div>
                          </div>
                        )}
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Campo Adicional 1</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Etiqueta campo adicional"
                              value={raffleFormAdditionalField1}
                              onChange={(e) =>
                                setRaffleFormAdditionalField1(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Valores Campo Adicional 1</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Valores separados por ; (para dropdown) o vacío (para texto libre)"
                              value={raffleFormAdditionalField1Values}
                              onChange={(e) =>
                                setRaffleFormAdditionalField1Values(
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Campo Adicional 2</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Etiqueta campo adicional"
                              value={raffleFormAdditionalField2}
                              onChange={(e) =>
                                setRaffleFormAdditionalField2(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Valores Campo Adicional 2</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Valores separados por ; (para dropdown) o vacío (para texto libre)"
                              value={raffleFormAdditionalField2Values}
                              onChange={(e) =>
                                setRaffleFormAdditionalField2Values(
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Campo Adicional 3</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Etiqueta campo adicional"
                              value={raffleFormAdditionalField3}
                              onChange={(e) =>
                                setRaffleFormAdditionalField3(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Valores Campo Adicional 3</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Valores separados por ; (para dropdown) o vacío (para texto libre)"
                              value={raffleFormAdditionalField3Values}
                              onChange={(e) =>
                                setRaffleFormAdditionalField3Values(
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-block btn-outline-danger btn-lg"
                          onClick={saveRaffleForm}
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function b64ToBlob(base64, contentType) {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++)
    byteNumbers[i] = byteChars.charCodeAt(i);
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], {
    type: contentType || "application/octet-stream",
  });
}
